import React from 'react'
import { useDocumentTitle } from '@uidotdev/usehooks'
import {
  type AlertColor,
  AppBar,
  Chip,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Slide,
  Stack,
  Toolbar,
  Typography
} from '@mui/material'
import {
  Cached,
  Close,
  DashboardCustomize,
  Engineering,
  InfoRounded,
  OpenInNew,
  Save,
  Settings,
  SettingsApplications,
  SportsHockeyTwoTone,
  Upgrade
} from '@mui/icons-material'
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions'
import { type TransitionProps } from '@mui/material/transitions'
import { alpha, styled } from '@mui/material/styles'
import {
  type FavoriteNhlTeamProfile,
  type FavoriteNhlTeamSelection
} from 'features/settings/nhlTeamSelection'
import { useSnackbar } from 'hooks/notification/UseSnackbar'
import { ServiceWorkerAppCacheName } from 'hooks/browser/useLocalStorage'
import PropTypes from 'prop-types'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ApiQueryKeys, ApiQueryService } from 'services/query/apiQueryService'
import { ApiMutationKeys, ApiMutationService } from 'services/mutation/apiMutationService'
import PageBase from 'components/page-title/PageBase'
import TeamImage from 'components/shared-components/TeamImage'
import LazyImage from 'components/shared-components/LazyImage'
import { useNavigate } from 'react-router'

const SettingsButton = styled(Button)({
  minWidth: 'calc(120px + 0.5dvw)'
})

const TransitionUp = React.forwardRef(function Transition (
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction='up' exit ref={ref} {...props} />
})

interface SelectFavoriteNhlTeamDialogProps {
  isOpen: boolean
  onClose: () => void
  nhlTeams: FavoriteNhlTeamSelection[]
  currentTeamId: number
  teamUpdated: (teamId: number) => void
  getTeamName: (teamId: number) => string
  openSnackbar: (message: string, severity: AlertColor) => void
}

const SelectFavoriteNhlTeamDialog: React.FC<SelectFavoriteNhlTeamDialogProps> = React.memo(({
  isOpen,
  onClose,
  nhlTeams,
  currentTeamId,
  teamUpdated,
  getTeamName,
  openSnackbar
}) => {
  const [nhlTeamId, setNhlTeamId] = React.useState<number>(currentTeamId)
  const { mutateAsync: saveNhlTeamAsync, isPending: isSavingTeam } = useMutation({
    mutationKey: [ApiMutationKeys.SetFavoriteNhlTeamMutation],
    mutationFn: async () => {
      await ApiMutationService[ApiMutationKeys.SetFavoriteNhlTeamMutation](nhlTeamId)
    }
  })

  React.useEffect(() => {
    setNhlTeamId(currentTeamId)
  }, [currentTeamId, isOpen])

  const saveNhlTeam = React.useCallback(async () => {
    await saveNhlTeamAsync()
    teamUpdated(nhlTeamId)
    onClose()
    const teamName = getTeamName(nhlTeamId)
    openSnackbar(`Team updated to the ${teamName}`, 'success')
  }, [nhlTeamId, teamUpdated, onClose, getTeamName, openSnackbar])

  return (
    <Dialog
      keepMounted
      fullScreen
      open={isOpen}
      disableEscapeKeyDown
      onClose={onClose}
      TransitionComponent={TransitionUp}
    >
      <AppBar
        color='primary'
        sx={{
          position: 'sticky',
          borderBottom: (theme) => `1px solid ${alpha(theme.palette.common.white, 0.18)}`,
          background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
        }}
      >
        <Toolbar>
          <IconButton edge='start' color='inherit' onClick={onClose} aria-label='close'>
            <Close />
          </IconButton>
          <Box ml={2} />
          <SportsHockeyTwoTone />
          <Typography sx={{ display: { sm: 'none', xs: 'none', md: 'block' }, ml: 2, flex: 1 }} variant='h6' component='div'>
            Select Your Favorite Team
          </Typography>
          <Typography sx={{ display: { sm: 'block', xs: 'block', md: 'none' }, ml: 2, flex: 1 }} variant='h6' component='div'>
            Select Your Team
          </Typography>
          <SettingsButton
            startIcon={<Save />}
            variant='outlined'
            autoFocus
            color='inherit'
            disabled={isSavingTeam}
            onClick={() => { void saveNhlTeam() }}
          >
            Save
          </SettingsButton>
        </Toolbar>
      </AppBar>
      <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
      <Paper sx={{ p: 1, borderRadius: 3, border: (theme) => `1px solid ${alpha(theme.palette.info.main, 0.25)}` }}>
      <List>
        {nhlTeams.map((team) => (
          <ListItem
          sx={{
            py: 0.35,
            borderRadius: 2,
            backgroundColor: team.teamId === nhlTeamId ? alpha('#5DADE2', 0.15) : 'inherit'
          }}
          key={team.teamId} disablePadding>
            <ListItemButton
              selected={nhlTeamId === team.teamId}
              onClick={() => { setNhlTeamId(team.teamId) }}
              sx={{
                borderRadius: 2,
                '&.Mui-selected': {
                  bgcolor: alpha('#5DADE2', 0.22)
                }
              }}
            >
              <ListItemIcon>
                <Checkbox
                  edge='start'
                  color='success'
                  checked={nhlTeamId === team.teamId}
                  tabIndex={-1}
                />
              </ListItemIcon>
              <TeamImage src={team.logoUri} alt={team.teamName} height={48} />
              <Box ml={2} />
              <ListItemText
                primary={team.teamName}
                secondary={nhlTeamId === team.teamId ? <Chip size='small' color='info' label='Selected' sx={{ mt: 0.5 }} /> : undefined}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      </Paper>
      </Box>
    </Dialog>
  )
})

SelectFavoriteNhlTeamDialog.displayName = 'SelectFavoriteNhlTeamDialog'
SelectFavoriteNhlTeamDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  nhlTeams: PropTypes.array.isRequired,
  currentTeamId: PropTypes.number.isRequired,
  teamUpdated: PropTypes.func.isRequired,
  getTeamName: PropTypes.func.isRequired,
  openSnackbar: PropTypes.func.isRequired
}

const AppSettings: React.FC = () => {
  useDocumentTitle('FantaHockey | Settings')

  const { openSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const [isConnectedToYahoo, setIsConnectedToYahoo] = React.useState<boolean>(false)
  const [nhlTeams, setNhlTeams] = React.useState<FavoriteNhlTeamSelection[]>([])
  const [isNhlDialogOpen, setIsNhlDialogOpen] = React.useState<boolean>(false)
  const [favoriteNhlTeamId, setFavoriteNhlTeamId] = React.useState<number>(0)
  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const { refetch: getIsYahooConnected } = useQuery<boolean>({
    queryKey: [ApiQueryKeys.IsUserConnectedToYahooQuery],
    queryFn: async () => {
      return (await ApiQueryService[ApiQueryKeys.IsUserConnectedToYahooQuery]()).data
    },
    enabled: false
  })
  const { refetch: getAllNhlTeams } = useQuery<FavoriteNhlTeamSelection[]>({
    queryKey: [ApiQueryKeys.GetAllNhlTeamsQuery],
    queryFn: async () => {
      return (await ApiQueryService[ApiQueryKeys.GetAllNhlTeamsQuery]()).data
    },
    enabled: false
  })
  const { refetch: getFavoriteNhlTeamSelection } = useQuery<FavoriteNhlTeamProfile>({
    queryKey: [ApiQueryKeys.GetUsersFavoriteNhlTeamQuery],
    queryFn: async () => {
      return (await ApiQueryService[ApiQueryKeys.GetUsersFavoriteNhlTeamQuery]()).data
    },
    enabled: false
  })

  React.useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const [isConnectedToYahoo, allNhlTeams, favoriteNhlTeamSelection] = await Promise.all([
        (await getIsYahooConnected()).data,
        (await getAllNhlTeams()).data,
        (await getFavoriteNhlTeamSelection()).data
      ])
      setIsConnectedToYahoo(isConnectedToYahoo ?? false)
      setNhlTeams(allNhlTeams ?? Array.from<FavoriteNhlTeamSelection>([]))
      setFavoriteNhlTeamId(favoriteNhlTeamSelection?.nhlTeamId ?? 0)
      setIsLoading(false)
    }
    void fetchData()
  }, [])

  const SettingsContentLoader = (): JSX.Element => {
    return (
      <Box px={2} py={3}>
        <Paper sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
          <CircularProgress color='info' />
          <Typography variant='body2' color='text.secondary' mt={1.5}>
            Loading your settings...
          </Typography>
        </Paper>
      </Box>
    )
  }

  const openNhlDialog = (): void => {
    setIsNhlDialogOpen(true)
  }

  const closeNhlDialog = (): void => {
    setIsNhlDialogOpen(false)
  }

  const getTeamName = React.useCallback(
    (teamId: number): string => {
      const team = nhlTeams.find((team) => team.teamId === teamId)
      return (team != null) ? team.teamName : ''
    },
    [nhlTeams]
  )

  const getTeamLogoUri = React.useCallback(
    (teamId: number): string => {
      const team = nhlTeams.find((team) => team.teamId === teamId)
      return (team != null) ? team.logoUri : ''
    },
    [nhlTeams]
  )

  const clearAppServiceWorkerCache = async (): Promise<void> => {
    await caches.delete(ServiceWorkerAppCacheName)
    openSnackbar('Cache cleared', 'info')
  }

  return (
    <>
      <PageBase icon={Settings} title='Settings'>
        {isLoading
          ? <SettingsContentLoader />
          : (
          <Box px={{ xs: 1.5, md: 2.5 }} py={1.5}>
            <Paper
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                mb: 2,
                border: (theme) => `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
                background: (theme) =>
                  `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.7)} 0%, ${alpha(theme.palette.background.paper, 0.96)} 60%, ${alpha(theme.palette.info.main, 0.35)} 100%)`
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent='space-between' alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Box>
                  <Typography variant='h5' fontFamily='Spantaran' sx={{ lineHeight: 1.1 }}>
                    Settings Control Center
                  </Typography>
                  <Typography variant='subtitle2' color='text.secondary' sx={{ mt: 0.75 }}>
                    Manage integrations, preferences, and app maintenance from one place.
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Chip
                    label={isConnectedToYahoo ? 'Yahoo Connected' : 'Yahoo Not Connected'}
                    color={isConnectedToYahoo ? 'success' : 'error'}
                  />
                  <Chip
                    icon={<SportsHockeyTwoTone />}
                    label={favoriteNhlTeamId !== 0 ? getTeamName(favoriteNhlTeamId) : 'No Favorite Team'}
                    color={favoriteNhlTeamId !== 0 ? 'info' : 'default'}
                  />
                </Stack>
              </Stack>
            </Paper>

            <Grid container spacing={2}>
              <Grid item xs={12} lg={7}>
                <Stack spacing={2}>
                  <Paper sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, border: (theme) => `1px solid ${alpha(theme.palette.success.main, 0.24)}` }}>
                    <Stack direction='row' spacing={1.25} alignItems='center' mb={1.5}>
                      <IntegrationInstructionsIcon />
                      <Typography variant='h6' fontFamily='Spantaran'>Integrations</Typography>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent='space-between'>
                      <Stack direction='row' spacing={1.5} alignItems='center'>
                        <LazyImage src='./assets/images/Yahoo!-Logo.wine.png' alt='yahoo' height={66} />
                        <Box>
                          <Typography fontWeight={700}>Yahoo Fantasy Sports</Typography>
                          <Typography variant='body2' color='text.secondary'>
                            {isConnectedToYahoo ? 'Connected and ready for live sync.' : 'Not connected. Link your account to enable full features.'}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack direction={{ xs: 'row', sm: 'column' }} spacing={1} alignItems={{ xs: 'center', sm: 'flex-end' }}>
                        <Chip label={isConnectedToYahoo ? 'Connected' : 'Not Connected'} color={isConnectedToYahoo ? 'success' : 'error'} />
                        <Button
                          size='small'
                          variant='outlined'
                          color='info'
                          endIcon={<OpenInNew />}
                          onClick={() => { navigate('/connect-my-fantasy-team') }}
                        >
                          Manage
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>

                  <Paper sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, border: (theme) => `1px solid ${alpha(theme.palette.info.main, 0.28)}` }}>
                    <Stack direction='row' spacing={1.25} alignItems='center' mb={1.5}>
                      <SettingsApplications />
                      <Typography variant='h6' fontFamily='Spantaran'>Preferences</Typography>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent='space-between'>
                      <Box>
                        <Typography fontWeight={700}>Favorite NHL Team</Typography>
                        <Stack direction='row' spacing={1.5} alignItems='center' mt={1}>
                          {favoriteNhlTeamId !== 0
                            ? (
                            <>
                              <LazyImage
                                src={getTeamLogoUri(favoriteNhlTeamId)}
                                height={34}
                                alt={getTeamName(favoriteNhlTeamId)}
                              />
                              <Typography>{getTeamName(favoriteNhlTeamId)}</Typography>
                            </>
                              )
                            : <Typography color='text.secondary'>No team selected</Typography>}
                        </Stack>
                      </Box>
                      <SettingsButton onClick={openNhlDialog} variant='contained' color='info' startIcon={<Upgrade />}>
                        {favoriteNhlTeamId !== 0 ? 'Update Team' : 'Select Team'}
                      </SettingsButton>
                    </Stack>
                  </Paper>

                  <Paper sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, border: (theme) => `1px solid ${alpha(theme.palette.warning.main, 0.28)}` }}>
                    <Stack direction='row' spacing={1.25} alignItems='center' mb={1.5}>
                      <Engineering />
                      <Typography variant='h6' fontFamily='Spantaran'>Maintenance</Typography>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent='space-between'>
                      <Box>
                        <Typography fontWeight={700}>Clear App Cache</Typography>
                        <Typography variant='body2' color='text.secondary'>
                          Remove cached assets and force a fresh app load on next navigation.
                        </Typography>
                      </Box>
                      <SettingsButton
                        startIcon={<Cached />}
                        onClick={() => { void clearAppServiceWorkerCache() }}
                        variant='outlined'
                        color='info'
                      >
                        Clear Cache
                      </SettingsButton>
                    </Stack>
                  </Paper>
                </Stack>
              </Grid>

              <Grid item xs={12} lg={5}>
                <Paper sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, height: '100%', border: (theme) => `1px solid ${alpha(theme.palette.primary.light, 0.25)}` }}>
                  <Stack direction='row' spacing={1.25} alignItems='center' mb={1.25}>
                    <DashboardCustomize />
                    <Typography variant='h6' fontFamily='Spantaran'>About FantaHockey</Typography>
                  </Stack>
                  <>
                    <Typography variant='body2' color='text.secondary' mb={1.5}>
                    Create smarter teams, make confident roster moves, and outmaneuver your league using real-time intelligence powered by Yahoo and NHL data.
                    Whether you’re managing daily lineups or planning long-term strategy, FantaHockey turns complex stats into clear, actionable insights. Our AI analyzes performance trends, matchups, and league context to give you an edge where it matters most.                  </Typography>
                    <Typography variant='body2' color='text.secondary' mb={1.5}>
                      AI-powered insights support trade decisions, lineup optimization, and player comparisons tailored to your league context.
                    </Typography>
                    <Typography variant='body2' color='text.secondary' mb={1.5}>
                      Trade smarter - Evaluate trades instantly with AI-driven projections tailored to your scoring format and roster needs.
                    </Typography>
                    <Typography variant='body2' color='text.secondary' mb={1.5}>
                      Optimize your lineup - Get matchup-aware lineup recommendations that maximize weekly and daily point potential.
                    </Typography>
                    <Typography variant='body2' color='text.secondary' mb={1.5}>
                      Compare players with context - Go beyond raw stats. See performance trends, role changes, and situational advantages.
                    </Typography>
                    <Typography variant='body2' color='text.secondary' mb={1.5}>
                      League-aware intelligence - Insights adapt to your specific league rules, categories, and roster structure.
                    </Typography>
                    <Typography variant='body2' color='text.secondary' mb={1.5}>
                      Real-time data integration - Stay updated with live stats and performance signals from trusted hockey data sources.
                    </Typography>
                    <Typography variant='body2' color='text.secondary' mb={1.5}>
                      FantaHockey is built for competitive fantasy managers who want deeper analysis without the spreadsheet grind. Whether you’re chasing playoffs or defending a title, the app helps you make sharper decisions, faster.
                    </Typography>
                  </>
                  <Paper sx={{ p: 1.5, borderRadius: 2, border: (theme) => `1px solid ${alpha(theme.palette.info.main, 0.2)}`, bgcolor: (theme) => alpha(theme.palette.primary.dark, 0.45) }}>
                    <Stack direction='row' spacing={1} alignItems='flex-start'>
                      <InfoRounded fontSize='small' sx={{ mt: 0.3 }} />
                      <Typography variant='caption' color='text.secondary'>
                        Disclaimer: FantaHockey does not own external data from Yahoo, the NHL, or other third-party providers. All data rights belong to their respective owners.
                      </Typography>
                    </Stack>
                  </Paper>
                </Paper>
              </Grid>
            </Grid>

            <SelectFavoriteNhlTeamDialog
              isOpen={isNhlDialogOpen}
              onClose={closeNhlDialog}
              nhlTeams={nhlTeams}
              currentTeamId={favoriteNhlTeamId}
              teamUpdated={setFavoriteNhlTeamId}
              getTeamName={getTeamName}
              openSnackbar={openSnackbar}
            />
          </Box>
            )}
      </PageBase>
    </>
  )
}

export default React.memo(AppSettings)
